import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const CustomerPortalContext = createContext({});

export const useCustomerPortal = () => {
  const context = useContext(CustomerPortalContext);
  if (!context) {
    throw new Error('useCustomerPortal must be used within CustomerPortalProvider');
  }
  return context;
};

const SESSION_KEY = 'customer_portal_session';

export const CustomerPortalProvider = ({ children }) => {
  const [customerSession, setCustomerSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore session from localStorage
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if session is still valid (24h expiry)
        if (parsed?.expiresAt && new Date(parsed.expiresAt) > new Date()) {
          setCustomerSession(parsed);
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
    }
    setLoading(false);
  }, []);

  // Request OTP for a given email
  const requestOtp = async (email) => {
    if (!email?.trim()) {
      return { error: 'Please enter your email address.' };
    }

    // Look up customer_id for this email
    const { data: customerId, error: lookupError } = await supabase
      .rpc('get_customer_id_for_portal_email', { p_email: email.trim().toLowerCase() });

    if (lookupError) {
      console.error('Portal email lookup error:', lookupError);
      return { error: 'Unable to process request. Please try again.' };
    }

    if (!customerId) {
      // Return generic message to avoid email enumeration
      return { success: true };
    }

    // Generate 6-digit OTP
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString(); // 15 min

    const { error: insertError } = await supabase
      .from('customer_portal_sessions')
      .insert({
        email: email.trim().toLowerCase(),
        customer_id: customerId,
        otp_code: otp,
        otp_expires_at: expiresAt,
        is_used: false,
      });

    if (insertError) {
      console.error('OTP insert error:', insertError);
      return { error: 'Unable to send verification code. Please try again.' };
    }

    // Send OTP email via dedicated edge function
    try {
      // Fetch customer name for the email
      const { data: customerData } = await supabase
        .from('customers')
        .select('name, organization_id')
        .eq('id', customerId)
        .single();

      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-portal-otp', {
        body: {
          customerEmail: email.trim().toLowerCase(),
          customerName: customerData?.name || null,
          otpCode: otp,
          organizationId: customerData?.organization_id || null,
        },
      });

      if (emailError) {
        console.error('[CustomerPortal] OTP email invocation error:', emailError);
        return { error: 'Unable to send verification code. Please try again or contact support.' };
      }

      if (emailResult?.error) {
        console.error('[CustomerPortal] OTP email send failed:', emailResult.error);
        return { error: 'Unable to send verification code. Please try again or contact support.' };
      }

      console.log('[CustomerPortal] OTP email sent successfully. EmailId:', emailResult?.emailId);
    } catch (emailErr) {
      console.error('[CustomerPortal] Failed to send OTP email:', emailErr);
      return { error: 'Unable to send verification code. Please try again or contact support.' };
    }

    console.log(`[CustomerPortal] OTP generated for ${email}`);
    return { success: true, devOtp: import.meta.env.DEV ? otp : undefined };
  };

  // Verify OTP and establish session
  const verifyOtp = async (email, otp) => {
    if (!email?.trim() || !otp?.trim()) {
      return { error: 'Please enter your email and verification code.' };
    }

    const { data: customerId, error: validateError } = await supabase
      .rpc('validate_customer_portal_otp', {
        p_email: email.trim().toLowerCase(),
        p_otp: otp.trim(),
      });

    if (validateError) {
      console.error('OTP validation error:', validateError);
      return { error: 'Verification failed. Please try again.' };
    }

    if (!customerId) {
      return { error: 'Invalid or expired verification code. Please request a new one.' };
    }

    // Fetch customer info
    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .select('id, name, email, company, organization_id')
      .eq('id', customerId)
      .single();

    if (customerError || !customer) {
      return { error: 'Unable to load customer information. Please try again.' };
    }

    const session = {
      customerId: customer.id,
      customerName: customer.name,
      customerEmail: email.trim().toLowerCase(),
      company: customer.company || customer.name,
      organizationId: customer.organization_id,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24h
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    setCustomerSession(session);
    return { success: true, session };
  };

  const signOut = () => {
    localStorage.removeItem(SESSION_KEY);
    setCustomerSession(null);
  };

  return (
    <CustomerPortalContext.Provider
      value={{
        customerSession,
        loading,
        isAuthenticated: !!customerSession,
        requestOtp,
        verifyOtp,
        signOut,
      }}
    >
      {children}
    </CustomerPortalContext.Provider>
  );
};

export default CustomerPortalProvider;
